import { ChevronUp, SearchMd, XClose } from "@untitled-ui/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";
import Input from "@/components/forms/Input";
import Badge from "@/components/text/Badge";
import Text from "@/components/text/Text";
import { cn } from "@/lib/utils";
import { type User } from "@/types";

import { useFetchUnreadConversationsCount } from "../services";
import MesssengerSearchSection from "./MessengerSearchSection";

export default function MessengerHeader({
  open,
  children,
  setOpen,
  onUserClick,
  showSearch = false,
  showToggle = true,
  className,
  searchClassName,
  searchHeaderClassName,
}: {
  open: boolean;
  children?: React.ReactNode;
  setOpen: (open: boolean) => void;
  onUserClick?: (user: User<true>) => void;
  showSearch?: boolean;
  showToggle?: boolean;
  className?: string;
  searchClassName?: string;
  searchHeaderClassName?: string;
}) {
  const { t } = useTranslation("messenger.user");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const { data: unreadCount } = useFetchUnreadConversationsCount();

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSearchMode(true);
  };

  const handleCloseSearch = () => {
    setIsSearchMode(false);
  };

  if (isSearchMode) {
    return (
      <div className="h-full">
        <MesssengerSearchSection
          className={searchClassName}
          headerClassName={searchHeaderClassName}
          onClose={handleCloseSearch}
          onUserClick={(user) => {
            onUserClick?.(user);
            setIsSearchMode(false);
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        open && children !== undefined && "border-b border-gray-200",
        "flex w-full shrink-0 cursor-pointer justify-between",
        showSearch && !showToggle ? "px-4 pt-8 pb-4" : "px-3.5",
        open && !(showSearch && !showToggle)
          ? "h-20 pt-7 pb-5"
          : !open
            ? "h-full rounded-xl border"
            : "",
        !showToggle ? "" : "rounded-t-xl",
        className,
      )}
      onClick={
        children === undefined || !open
          ? () => {
              setOpen(!open);
            }
          : undefined
      }
    >
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {open && children !== undefined ? (
          children
        ) : showSearch ? (
          <Input
            startIcon={<SearchMd className="size-5 text-gray-500" />}
            placeholder={t("Search", { ns: "core.shared" })}
            onClick={handleSearchClick}
          />
        ) : (
          <div className="flex items-center gap-2">
            <Text weight="semibold" size="xl" className="ps-2.5 leading-7">
              {t("Messages")}
            </Text>
            {unreadCount?.data?.unread_conversations_count !== undefined &&
              unreadCount.data.unread_conversations_count > 0 && (
                <Badge size="sm">
                  {unreadCount.data.unread_conversations_count}
                </Badge>
              )}
          </div>
        )}
      </div>
      {showToggle && (
        <div className="flex items-center gap-1">
          {open && !children && (
            <Button color="tertiary-gray" square onClick={handleSearchClick}>
              <SearchMd className="size-5 text-gray-500 transition-transform" />
            </Button>
          )}
          <Button color="tertiary-gray" square onClick={() => setOpen(!open)}>
            <ChevronUp
              className={cn(
                "hidden size-5 text-gray-500 transition-transform md:block",
                open && "rotate-180",
              )}
            />
            <XClose className="block size-5 text-gray-500 md:hidden" />
          </Button>
        </div>
      )}
    </div>
  );
}
